import { tick } from 'svelte';
import { derived, get, writable } from 'svelte/store';
import { sendPmEvent } from '$lib/services/gtm/methods';

import { initConversation, loadConversations } from '@api/room/conversation';
import { sendMessage } from '@api/room/message';
import type { Message, MessageReceived } from '@api/types';
import { sendPmStats } from '@api/user/stats';
import { URLS } from '@shared/routing';
import type { components } from '@shared/swagger';
import { EventType } from '@shared/types';

import type { Store, StoreState } from './types';

const initialValues: StoreState = {
    messages: [],
    conversations: [],
    activeConversation: undefined,
};

function createStore(): Store {
    const store = writable(initialValues);
    const { subscribe, update, set } = store;

    const updateStore = (data: Partial<StoreState>) => {
        update((state) => ({
            ...state,
            ...data,
        }));
    };

    return {
        subscribe,
        update,
        set,
        initConversation: async (userId: string) => {
            const id = await initConversation(userId);
            if (id) {
                return URLS.messages(id);
            }
            return URLS.myMessages();
        },
        setActiveConversation: (id: string | null): void => {
            update((state) => ({
                ...state,
                activeConversation: state.conversations.find(c => c.id === id),
                conversations: state.conversations
                    .map(c => c.id === id ? { ...c, unreadMessages: 0 } : c),
            }));
        },
        loadConversations: async (activeConversationId?: string) => {
            update((state) => ({
                ...state,
                isLoading: true,
            }));
            const nowState = get(store);
            const data = await loadConversations(
                activeConversationId ?? nowState.activeConversation?.id
            );
            update((state) => ({
                ...state,
                conversations: data.items,
            }));
            await tick();
        },
        sendMessage: async (
            data: Message,
            statsData: components['schemas']['CategoryData']['pm'],
            isDesktop: boolean
        ) => {
            sendMessage(data);
            if (!data.roomId) {
                sendPmEvent({ event: 'pm_sent' });
                await sendPmStats(EventType.PmSent, statsData, isDesktop);
            }
        },
        addMessage: async (data) => {
            const { roomId, conversationId } = data;
            const nowState = get(store);
            const lastMessage = data.type !== 'call' ? { lastMessage: data } : {};
            const existConversation = !roomId && conversationId
                ? nowState.conversations.find(c => c.id === conversationId)
                : null;
            if (!existConversation && !roomId && conversationId) {
                const newData = await loadConversations(nowState.activeConversation?.id);
                update((state) => ({
                    ...state,
                    activeConversation: state.activeConversation
                        ? newData.items.find(i => i.id === state.activeConversation?.id)
                        : state.activeConversation,
                    conversations: newData.items,
                }));
                return;
            }
            update((state) => {
                if (roomId || conversationId === state.activeConversation?.id) {
                    return {
                        ...state,
                        activeConversation: state.activeConversation
                            ? { ...state.activeConversation, ...lastMessage }
                            : state.activeConversation,
                        conversations: state.conversations
                            .map(c => c.id === conversationId ? { ...c, ...lastMessage } : c),
                        messages: state.messages.filter(m => m.id !== data.id).concat([data]),
                    };
                }
                if (existConversation) {
                    const updatedConversation = {
                        ...existConversation,
                        ...lastMessage,
                        unreadMessages: existConversation.unreadMessages + 1,
                    };
                    return {
                        ...state,
                        activeConversation: state.activeConversation?.id === existConversation?.id
                            ? updatedConversation
                            : state.activeConversation,
                        conversations: state.conversations.map(c => c.id === conversationId ? updatedConversation : c),
                    };
                }
                return state;
            });
        },
        translateMessage: (data) => {
            update((state) => {
                const messages: MessageReceived[] = state.messages;
                messages.forEach((message, index) => {
                    if (message.id === data.id && messages[index].data?.text) {
                        messages[index].data!.text = {
                            ...messages[index].data!.text,
                            translatedLanguage: data.translatedLanguage,
                            translatedText: data.translatedText,
                        };
                    }
                });
                return {
                    ...state,
                    messages,
                };
            });
        },
        onMessageButtonClick: (messageId: string) => {
            update((state) => {
                const messages = state.messages.map(
                    (m) => m.id === messageId && m.giftRequested
                        ? { ...m, giftRequested: { ...(m.giftRequested ?? {}), hasResponse: true } }
                        : m
                );
                return {
                    ...state,
                    messages,
                };
            });
        },
        onMessageButtonAdd: (messageId: string, gift: NonNullable<MessageReceived['gift']>) => {
            update((state) => {
                return {
                    ...state,
                    messages: state.messages.map(
                        (m) => m.id === messageId
                            ? { ...m, giftRequested: { gift } }
                            : m
                    ),
                };
            });
        },
        clearMessages: () => {
            update((state) => {
                return {
                    ...state,
                    activeConversation: state.activeConversation
                        ? { ...state.activeConversation, lastMessage: undefined }
                        : state.activeConversation,
                    conversations: state.conversations.map(
                        c => c.id === state.activeConversation?.id ? { ...c, lastMessage: undefined } : c
                    ),
                    messages: [],
                };
            });
        },
        addMessages: (data: MessageReceived[], append = false) => {
            update((state) => {
                return {
                    ...state,
                    messages: append ? data.concat(state.messages) : data,
                };
            });
        },

        changePartnerIsBlocked: (partnerId: string, val: boolean) => {
            const state = get(store);
            const activeConv = state.activeConversation;
            const activeConvPartner = state.activeConversation?.partner;

            updateStore({
                activeConversation: activeConv && activeConvPartner?.id === partnerId ? {
                    ...activeConv,
                    partner: {
                        ...activeConvPartner,
                        isBlocked: val,
                    },
                } : activeConv,
                conversations: state.conversations.map(c => {
                    if (c.partner.id === partnerId) {
                        return {
                            ...c,
                            partner: {
                                ...c.partner,
                                isBlocked: val,
                            },
                        };
                    }

                    return c;
                }),
            });
        },

        changePartnerIsBlockedBy: (partnerId: string, val: boolean) => {
            const state = get(store);
            const activeConv = state.activeConversation;
            const activeConvPartner = state.activeConversation?.partner;

            updateStore({
                activeConversation: activeConv && activeConvPartner?.id === partnerId ? {
                    ...activeConv,
                    partner: {
                        ...activeConvPartner,
                        isBlockedBy: val,
                    },
                } : activeConv,
                conversations: state.conversations.map(c => {
                    if (c.partner.id === partnerId) {
                        return {
                            ...c,
                            partner: {
                                ...c.partner,
                                isBlockedBy: val,
                            },
                        };
                    }

                    return c;
                }),
            });
        },
    };
}

export const messageStore = createStore();
